/**
 * pp-product.js — PP Jeweller product page interactive functionality
 * Self-contained IIFE, no dependencies, no jQuery.
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // 1. Stacked Gallery Thumbnails
  // ---------------------------------------------------------------------------
  function initGallery() {
    const galleryScroll = document.getElementById('pp-gallery-scroll');
    if (!galleryScroll) return;

    const thumbBtns = document.querySelectorAll('.pp-thumb-btn');
    const stackImgs = document.querySelectorAll('.pp-stack-img');

    if (!thumbBtns.length || !stackImgs.length) return;

    // Click: scroll gallery to the matching image
    thumbBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        const index = btn.dataset.index;
        const target = galleryScroll.querySelector('.pp-stack-img[data-index="' + index + '"]');
        if (!target) return;

        galleryScroll.scrollTo({
          top: target.offsetTop - galleryScroll.offsetTop,
          behavior: 'smooth'
        });

        setActiveThumb(index);
      });
    });

    // IntersectionObserver: update active thumbnail as user scrolls
    const observerOptions = {
      root: galleryScroll,
      rootMargin: '0px',
      threshold: 0.5
    };

    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          const index = entry.target.dataset.index;
          setActiveThumb(index);
        }
      });
    }, observerOptions);

    stackImgs.forEach(function (img) {
      observer.observe(img);
    });

    function setActiveThumb(index) {
      thumbBtns.forEach(function (btn) {
        const isActive = btn.dataset.index === index;
        btn.classList.toggle('active', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }
  }

  // ---------------------------------------------------------------------------
  // 2. Tabs
  // ---------------------------------------------------------------------------
  function initTabs() {
    const tabsSection = document.getElementById('pp-tabs-section');
    if (!tabsSection) return;

    const tabBtns = tabsSection.querySelectorAll('.pp-tab-btn');
    const tabPanels = tabsSection.querySelectorAll('.pp-tab-panel');
    if (!tabBtns.length) return;

    const productId = tabsSection.dataset.productId || 'default';
    const storageKey = 'pp_active_tab_' + productId;

    function activateTab(tabId) {
      tabBtns.forEach(function (btn) {
        const isActive = btn.dataset.tab === tabId;
        btn.classList.toggle('active', isActive);
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      tabPanels.forEach(function (panel) {
        const isActive = panel.id === tabId;
        panel.classList.toggle('active', isActive);
        panel.hidden = !isActive;
      });

      try {
        sessionStorage.setItem(storageKey, tabId);
      } catch (_) { /* storage may be unavailable */ }
    }

    tabBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        activateTab(btn.dataset.tab);
      });
    });

    // Restore saved tab or default to first
    let savedTab = null;
    try {
      savedTab = sessionStorage.getItem(storageKey);
    } catch (_) { /* ignore */ }

    const firstTabId = tabBtns[0]?.dataset.tab;
    const tabToActivate = savedTab || firstTabId;
    if (tabToActivate) {
      activateTab(tabToActivate);
    }

    // Expose activateTab for use by other features
    return activateTab;
  }

  // ---------------------------------------------------------------------------
  // 3. Price Breakup CTA
  // ---------------------------------------------------------------------------
  function initPriceBreakupCta(activateTab) {
    const pbCta = document.getElementById('pp-pb-cta');
    const tabsSection = document.getElementById('pp-tabs-section');
    const infoCol = document.getElementById('pp-info-col');

    if (!pbCta || !tabsSection) return;

    pbCta.addEventListener('click', function () {
      // Activate the Price Break Up tab
      if (typeof activateTab === 'function') {
        activateTab('tab-price-breakup');
      }

      // Scroll tabs section into view within the info panel if possible
      const scrollContainer = infoCol || window;

      if (infoCol && typeof infoCol.scrollTo === 'function') {
        const offset = tabsSection.offsetTop - infoCol.offsetTop;
        infoCol.scrollTo({ top: offset, behavior: 'smooth' });
      } else {
        tabsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      // Flash effect
      tabsSection.classList.add('pp-flash');
      setTimeout(function () {
        tabsSection.classList.remove('pp-flash');
      }, 500);
    });
  }

  // ---------------------------------------------------------------------------
  // 4. Customise Modal
  // ---------------------------------------------------------------------------
  function initCustomiseModal() {
    const modal = document.getElementById('pp-customise-modal');
    if (!modal) return;

    const openBtn = document.getElementById('pp-customise-btn');
    const closeBtn = document.getElementById('pp-customise-close');
    const confirmBtn = document.getElementById('pp-customise-confirm');
    const nameInput = document.getElementById('pp-custom-name');
    const engravedText = document.getElementById('pp-engraved-text');
    const charLeft = document.getElementById('pp-char-left');
    const MAX_CHARS = 14;

    // Open
    openBtn?.addEventListener('click', function () {
      modal.classList.add('open');
      document.body.style.overflow = 'hidden';
      nameInput?.focus();
    });

    // Close helpers
    function closeModal() {
      modal.classList.remove('open');
      document.body.style.overflow = '';
    }

    closeBtn?.addEventListener('click', closeModal);

    // Click on backdrop (the modal overlay, not the inner panel)
    modal.addEventListener('click', function (e) {
      if (e.target === modal) {
        closeModal();
      }
    });

    // Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('open')) {
        closeModal();
      }
    });

    // Name input → live engraving preview
    nameInput?.addEventListener('input', function () {
      const val = nameInput.value.slice(0, MAX_CHARS);
      nameInput.value = val; // enforce max in-place
      if (engravedText) engravedText.textContent = val || 'Your Name';
      if (charLeft) charLeft.textContent = MAX_CHARS - val.length;
    });

    // Font style buttons
    document.querySelectorAll('.pp-font-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        document.querySelectorAll('.pp-font-btn').forEach(function (b) {
          b.classList.remove('active');
        });
        btn.classList.add('active');
        if (engravedText) engravedText.style.fontFamily = btn.dataset.font || '';
      });
    });

    // Font size buttons
    document.querySelectorAll('.pp-size-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        document.querySelectorAll('.pp-size-btn').forEach(function (b) {
          b.classList.remove('active');
        });
        btn.classList.add('active');
        const size = btn.dataset.size;
        if (engravedText && size) engravedText.style.fontSize = size + 'px';
      });
    });

    // Confirm
    confirmBtn?.addEventListener('click', function () {
      const activeFont = document.querySelector('.pp-font-btn.active');
      const activeSize = document.querySelector('.pp-size-btn.active');
      const name = nameInput?.value || '';
      const font = activeFont?.dataset.font || '';
      const size = activeSize?.dataset.size || '';

      window.ppCustomisation = { name, font, size };

      if (openBtn) {
        openBtn.textContent = name
          ? '✓ Customised: ' + name
          : '✓ Customised';
      }

      closeModal();
    });

    // Metal sync: listen for variant:change or bracelet bar data-metal changes
    const braceletBar = document.getElementById('pp-bracelet-bar');

    document.addEventListener('variant:change', function (e) {
      const metal = e.detail?.metal;
      if (braceletBar && metal) {
        braceletBar.className = 'pp-bracelet-bar pp-metal-' + metal;
      }
    });

    if (braceletBar) {
      const metalObserver = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'data-metal') {
            const metal = braceletBar.dataset.metal;
            if (metal) {
              braceletBar.className = 'pp-bracelet-bar pp-metal-' + metal;
            }
          }
        });
      });

      metalObserver.observe(braceletBar, { attributes: true });
    }
  }

  // ---------------------------------------------------------------------------
  // 5. Delivery / Pincode Checker — handled inline in main-product.liquid
  // ---------------------------------------------------------------------------
  function initDeliveryChecker() {}

  // ---------------------------------------------------------------------------
  // 6. Coupon Copy Chips
  // ---------------------------------------------------------------------------
  function initCouponCopy() {
    document.querySelectorAll('.pp-copy-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const code = btn.dataset.code;
        if (!code) return;

        const originalText = btn.textContent;

        navigator.clipboard.writeText(code).then(function () {
          btn.textContent = '✓ Copied!';
          btn.setAttribute('aria-label', 'Copied ' + code);
          setTimeout(function () {
            btn.textContent = originalText;
            btn.setAttribute('aria-label', 'Copy code ' + code);
          }, 1800);
        }).catch(function () {
          // Fallback for older browsers / non-secure contexts
          try {
            const tmp = document.createElement('textarea');
            tmp.value = code;
            tmp.style.position = 'fixed';
            tmp.style.opacity = '0';
            document.body.appendChild(tmp);
            tmp.select();
            document.execCommand('copy');
            document.body.removeChild(tmp);

            btn.textContent = '✓ Copied!';
            setTimeout(function () {
              btn.textContent = originalText;
            }, 1800);
          } catch (_) { /* silent fail */ }
        });
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Bootstrap
  // ---------------------------------------------------------------------------
  document.addEventListener('DOMContentLoaded', function () {
    initGallery();
    var activateTab = initTabs();
    initPriceBreakupCta(activateTab);
    initCustomiseModal();
    initDeliveryChecker();
    initCouponCopy();
  });

})();
